import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOSchema from "@/components/feature/SEOSchema";
import { siteConfig } from "@/mocks/siteConfig";

const milestones = [
  { year: "2020", title: "mogu 正式成立", desc: "创始团队在北京中关村成立 mogu，致力于打造新一代智能协作平台" },
  { year: "2021", title: "获得 A 轮融资", desc: "完成 A 轮融资，产品进入快速迭代阶段，用户突破 10 万" },
  { year: "2022", title: "移动端全面上线", desc: "iOS 和 Android 应用同步发布，实现全平台覆盖，用户突破 50 万" },
  { year: "2023", title: "企业版发布", desc: "推出企业版方案，服务超过 100 家企业客户，通过 ISO 27001 安全认证" },
  { year: "2024", title: "AI 智能引擎上线", desc: "集成 AI 分析引擎，平台智能化水平大幅提升，活跃用户突破 80 万" },
  { year: "2025", title: "全球化布局", desc: "拓展海外市场，支持多语言版本，全球用户突破 100 万" },
  { year: "2026", title: "持续创新", desc: "发布 v3.8 版本，全面优化性能与体验，持续引领行业创新" },
];

const testimonials = [
  {
    name: "张伟",
    role: "某科技公司 CTO",
    content: "mogu 帮助我们团队实现了高效的远程协作，数据分析功能让决策更加科学。上线以来团队效率提升了 40%，强烈推荐！",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20corporate%20headshot%20of%20Chinese%20male%20tech%20executive%2C%20clean%20white%20background%2C%20warm%20friendly%20expression%2C%20modern%20professional%20portrait%20photography&width=100&height=100&seq=avatar-01&orientation=squarish",
  },
  {
    name: "李娜",
    role: "某电商平台运营总监",
    content: "从数据管理到自动化流程，mogu 让我们的运营工作变得前所未有的高效。特别是智能分析引擎，帮助我们发现了许多之前忽略的业务机会。",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20corporate%20headshot%20of%20Chinese%20female%20business%20director%2C%20clean%20white%20background%2C%20confident%20professional%20portrait%2C%20warm%20lighting%2C%20modern%20photography&width=100&height=100&seq=avatar-02&orientation=squarish",
  },
  {
    name: "王强",
    role: "某初创企业创始人",
    content: "作为一家初创公司，mogu 提供的性价比非常出色。我们用了 mogu 之后就不需要同时使用多款工具了，一站式搞定一切。",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20corporate%20headshot%20of%20Chinese%20male%20entrepreneur%20founder%2C%20clean%20white%20background%2C%20friendly%20professional%20portrait%2C%20modern%20photography&width=100&height=100&seq=avatar-03&orientation=squarish",
  },
];

export default function AboutPage() {
  return (
    <>
      <SEOSchema type="about" />
      <Navbar />
      <main className="min-h-screen pt-24 bg-white">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <Breadcrumb
            items={[
              { name: "首页", path: "/" },
              { name: "关于我们" },
            ]}
          />

          <section className="mt-8 mb-20">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于 {siteConfig.name}
            </h1>
            <p className="text-foreground-500 max-w-3xl leading-relaxed">
              {siteConfig.name}{" "}
              致力于通过技术创新，帮助企业和个人实现更高效的数字化协作与管理。我们相信，优秀的工具能够释放人的创造力。
            </p>
          </section>

          <section className="mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                  我们的故事
                </h2>
                <div className="text-foreground-600 leading-relaxed space-y-4">
                  <p>
                    {siteConfig.name} 诞生于 2020
                    年，由一群热爱技术、相信数据力量的工程师和产品设计师共同创立。我们看到了企业和团队在数字化转型过程中面临的挑战——工具碎片化、数据孤岛、协作低效——于是决定打造一个真正一站式的智能平台。
                  </p>
                  <p>
                    经过六年的持续打磨和迭代，{siteConfig.name}{" "}
                    已经从最初的协作工具成长为覆盖数据分析、自动化流程、团队管理等核心场景的综合平台，服务超过
                    100 万用户和 200 家企业客户。
                  </p>
                  <p>
                    我们始终坚持"用户至上、技术驱动"的理念，不断探索前沿技术，将人工智能、大数据分析等能力融入产品，帮助用户在快速变化的商业环境中保持竞争力。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20workspace%20with%20diverse%20team%20working%20together%2C%20bright%20natural%20light%2C%20clean%20minimalist%20office%20design%2C%20warm%20atmosphere%2C%20professional%20teamwork%20scene%2C%20creative%20energy%2C%20editorial%20photography&width=700&height=500&seq=about-story&orientation=landscape"
                  alt={`${siteConfig.name} 团队`}
                  title={`${siteConfig.name} 工作氛围 - mogu`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-foreground-200 md:-translate-x-px" />
              <div className="flex flex-col gap-8">
                {milestones.map((milestone, index) => (
                  <div
                    key={milestone.year}
                    className={`relative flex flex-col md:flex-row items-start gap-6 ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    <div className="md:w-1/2 flex justify-end md:justify-start">
                      <div
                        className={`ml-10 md:ml-0 ${
                          index % 2 === 0 ? "md:mr-8 md:text-right" : "md:ml-8"
                        }`}
                      >
                        <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-foreground-950 text-white mb-2 whitespace-nowrap">
                          {milestone.year}
                        </span>
                        <h3 className="text-base font-semibold text-foreground-950 mb-2">
                          {milestone.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">
                          {milestone.desc}
                        </p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-2.5 h-2.5 rounded-full bg-foreground-950 -translate-x-1/2 mt-1.5" />
                    <div className="md:w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, index) => (
                <div
                  key={index}
                  className="p-6 md:p-8 rounded-2xl bg-background-100 border border-foreground-100"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        title={`${t.name} - ${t.role}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{t.content}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="flex flex-col items-center p-8 rounded-2xl bg-background-100 border border-foreground-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-foreground-950 text-white mb-4">
                  <i className="ri-mail-line text-xl" />
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">电子邮件</h3>
                <a
                  href={`mailto:${siteConfig.email}`}
                  className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer"
                >
                  {siteConfig.email}
                </a>
              </div>

              <div className="flex flex-col items-center p-8 rounded-2xl bg-background-100 border border-foreground-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-foreground-950 text-white mb-4">
                  <i className="ri-phone-line text-xl" />
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">电话咨询</h3>
                <a
                  href={`tel:${siteConfig.phone}`}
                  className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer"
                >
                  {siteConfig.phone}
                </a>
              </div>

              <div className="flex flex-col items-center p-8 rounded-2xl bg-background-100 border border-foreground-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-foreground-950 text-white mb-4">
                  <i className="ri-map-pin-line text-xl" />
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">公司地址</h3>
                <p className="text-sm text-foreground-500">{siteConfig.address}</p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}